import { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Step,
  StepLabel,
  StepIconProps,
  DialogTitle,
  DialogTitleProps,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Button } from "../shared/Button/Button";
import {
  KitPickupBackButton,
  KitPickupStyledDialogActions,
  StepperWrapper,
} from "./KitPickup.styles";
import KitPickupInformation from "./NewKitPickupInformation";
import KitPickupConfirmation from "./NewKitPickupConfirmation";
import {
  KitPickupErrorDetails,
  KitStepper,
  NewKitPickupClinicAccount,
  NewKitPickupDetails,
  NewKitPickupOrder,
} from "./types";
import { SubmitNewKitPickup } from "./Services/NewKitPickupService";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { themePalette } from "../../theme";
import { TextWrap } from "../Grid/Grid.styles";
import { SendActionType } from "../shared/Services/SharedServices";
import { isAfterNoon, formatTimeToHHMM } from "../../utils/timeHelpers";

interface KitPickupProps {
  handleClose: () => void;
  newKitPickupOpen: boolean;
}

const getSteps = () => ["Order", "Confirmation", "Complete"];

const NewKitPickup: React.FC<KitPickupProps> = ({
  handleClose,
  newKitPickupOpen,
}) => {
  const { auth, setLoggedOut } = useAuthenticationHook();

  const [activeStep, setActiveStep] = useState(KitStepper.Order);

  // Error details tracks errors displayed by the UI
  const errorDetailsInitialState = {
    isInternationalAccountError: false,
    isEmptyFieldsError: false,
    isCustomerCloseTimeError: false,
    isReadyDateError: false,
    isReadyDateDayOfWeekError: false,
    isEarlyReadyTimeError: false,
    isSameDayPickupError: false,
    isAPIError: false,
  };
  const [errorDetails, setErrorDetails] = useState<KitPickupErrorDetails>(
    errorDetailsInitialState,
  );

  // Tracks user-modifiable pickup details like pickup window and provider name
  const newKitPickupDetailsInitialState = {
    ready_date: (() => {
      const now = new Date();
      if (isAfterNoon()) {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        return tomorrow.toISOString().split("T")[0];
      }
      return now.toISOString().split("T")[0];
    })(),
    // Add 10 minutes to ready time. FedEx API will reject anything earlier than the current time,
    // so we want to give users time to fill out the form
    ready_time: (() => {
      // Using the current time doesn't make sense if the date will be tomorrow. So default to 9am
      if (isAfterNoon()) {
        return "09:00";
      }
      return formatTimeToHHMM(new Date(new Date().getTime() + 10 * 60000));
    })(),
    customer_close_time: "17:00",
    person_name: `${auth?.first_name} ${auth?.last_name}`,
  };
  const [newKitPickupDetails, setNewKitPickupDetails] =
    useState<NewKitPickupDetails>(newKitPickupDetailsInitialState);

  // Tracks the current account displayed based on the clinic account dropdown
  const newKitPickupClinicAccountInitialState = {
    sfname: "",
    lims_id: "",
    billingstreet: "",
    billingcity: "",
    billingstatecode: "",
    billingpostalcode: "",
    billingcountrycode: "",
    sfid: "",
    phone: "",
    office_fax: "",
    is_standard_account: false,
  };
  const [newKitPickupClinicAccount, setNewKitPickupClinicAccount] =
    useState<NewKitPickupClinicAccount>(newKitPickupClinicAccountInitialState);

  // State used for submission and confirmation of orders
  const [newKitPickupOrder, setNewKitPickupOrder] =
    useState<NewKitPickupOrder>();
  const [newOrderCode, setNewOrderCode] = useState<string>("");

  // Reset all errors to default
  const resetErrorDetails = () => {
    setErrorDetails(errorDetailsInitialState);
  };

  // Reset all form state to default
  const resetFormState = () => {
    setNewKitPickupClinicAccount(newKitPickupClinicAccountInitialState);
    setNewKitPickupDetails(newKitPickupDetailsInitialState);
    setNewKitPickupOrder(undefined);
  };

  // Validate all form fields and set errors if any are invalid
  const validateAndSetErrors = () => {
    if (newKitPickupClinicAccount.billingcountrycode !== "US") {
      setErrorDetails({ ...errorDetails, isInternationalAccountError: true });
      return false;
    }

    // All required fields must be filled
    if (!newKitPickupDetails.ready_date || !newKitPickupDetails.ready_time) {
      setErrorDetails({ ...errorDetails, isEmptyFieldsError: true });
      return false;
    }

    if (!newKitPickupDetails.customer_close_time) {
      setErrorDetails({ ...errorDetails, isEmptyFieldsError: true });
      return false;
    }

    if (!newKitPickupDetails.person_name || !newKitPickupClinicAccount.sfname) {
      setErrorDetails({ ...errorDetails, isEmptyFieldsError: true });
      return false;
    }

    const readyDateTimestamp = new Date(
      `${newKitPickupDetails.ready_date} ${newKitPickupDetails.ready_time}`,
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    // Must concatenate time to prevent timezone conversion landing on the wrong day
    const readyDate = new Date(`${newKitPickupDetails.ready_date}T00:00:00`);

    // Date must be today or tomorrow
    if (readyDate < today || readyDate > tomorrow) {
      setErrorDetails({ ...errorDetails, isReadyDateError: true });
      return false;
    }
    // Time must be later than now
    if (readyDateTimestamp < new Date()) {
      setErrorDetails({ ...errorDetails, isEarlyReadyTimeError: true });
      return false;
    }
    // If the current time is later than noon, ready date must be tomorrow or later
    if (isAfterNoon() && readyDate <= today) {
      setErrorDetails({ ...errorDetails, isSameDayPickupError: true });
      return false;
    }

    // Check if the ready date is a weekend
    if (readyDate.getDay() === 0 || readyDate.getDay() === 6) {
      setErrorDetails({ ...errorDetails, isReadyDateDayOfWeekError: true });
      return false;
    }

    const customerCloseTimeTimestamp = new Date(
      `${newKitPickupDetails.ready_date} ${newKitPickupDetails.customer_close_time}`,
    );

    const latestReadyTime = new Date(customerCloseTimeTimestamp);
    latestReadyTime.setHours(latestReadyTime.getHours() - 3);

    // Customer close time must be at least 3 hours before the ready time
    if (readyDateTimestamp > latestReadyTime) {
      setErrorDetails({ ...errorDetails, isCustomerCloseTimeError: true });
      return false;
    }
    return true;
  };

  // State used to make sure only one API order can be requested at a time
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Prevent multiple submissions while request is in progress
    if (isSubmitting) {
      return;
    }

    resetErrorDetails();

    if (!validateAndSetErrors()) {
      setActiveStep(KitStepper.Order);
      return;
    }

    setIsSubmitting(true);

    try {
      // Fedex API is weird, it wants an ISO string but will ignore the timezone. So we manually build an ISO string
      // using date and time as they are in local time
      const readyDateTimestamp = `${newKitPickupDetails.ready_date}T${newKitPickupDetails.ready_time}:00`;
      const todayDate = new Date().toLocaleDateString("en-CA");
      const isFutureDay = newKitPickupDetails.ready_date > todayDate;

      const order = {
        ready_timestamp: readyDateTimestamp,
        customer_close_time: newKitPickupDetails.customer_close_time,
        person_name: newKitPickupDetails.person_name,
        account_lims_id: newKitPickupClinicAccount.lims_id,
        is_future_day: isFutureDay,
      };
      setNewKitPickupOrder(order);

      // Submit the order to the Fedex API
      const ret = await SubmitNewKitPickup(auth!.token, order, setLoggedOut);

      if (ret.success === "true") {
        // Set the order code for confirmation and move to the confirmation dialog
        setNewOrderCode(ret.confirmation_code);
        setActiveStep(KitStepper.Complete);
        await SendActionType(
          "ProviderKitPickupOrderPlaced",
          setLoggedOut,
          auth?.token,
          {
            lims_id: newKitPickupClinicAccount.lims_id,
            provider_name: newKitPickupDetails.person_name,
            account_name: newKitPickupClinicAccount.sfname,
          },
        );
      } else {
        setErrorDetails({
          ...errorDetails,
          isAPIError: true,
          apiErrorMessage: ret.error_message,
        });
        await SendActionType(
          "ProviderKitPickupOrderFailed",
          setLoggedOut,
          auth?.token,
          {
            lims_id: newKitPickupClinicAccount.lims_id,
            provider_name: newKitPickupDetails.person_name,
            account_name: newKitPickupClinicAccount.sfname,
          },
        );
      }
    } catch (error) {
      setErrorDetails({
        ...errorDetails,
        isAPIError: true,
        apiErrorMessage: undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // On the first page, the back button becomes a cancel button and closes the dialog
  const handleBackOrCancel = () => {
    if (activeStep === KitStepper.Order) {
      internalClose();
    } else {
      setActiveStep(KitStepper.Order);
      resetErrorDetails();
    }
  };

  // Only to the confirmation dialog if all fields are valid
  const handleNext = async () => {
    if (validateAndSetErrors()) {
      setActiveStep(KitStepper.Confirmation);
      await SendActionType(
        "ProviderKitPickupViewConfirm",
        setLoggedOut,
        auth?.token,
      );
    } else {
      // Scroll to the top of the dialog to help users address errors
      const dialogContent = document.querySelector(
        ".MuiDialog-paper .MuiDialogContent-root",
      );
      dialogContent?.scrollTo({ top: 0 });
    }
  };

  const internalClose = () => {
    handleClose();
  };

  // Styles modal title (matches the style used by the test order form)
  const NewKitPickupDialogTitle = (props: DialogTitleProps) => {
    const { children, ...other } = props;

    return (
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          backgroundColor: themePalette.neutral50,
          borderBottom: `1px solid ${themePalette.neutral100}`,
        }}
        {...other}
      >
        {children}
        <IconButton
          aria-label="close"
          onClick={() => internalClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: themePalette.redMedium,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
    );
  };

  // Styled step icon (matches the style used by the test order form)
  const StepIcon = (props: StepIconProps) => {
    const { active, completed } = props;

    if (active) {
      return (
        <RadioButtonUncheckedIcon
          sx={{
            color: themePalette.redMedium,
          }}
        />
      );
    }
    if (completed) {
      return (
        <CheckCircleIcon
          sx={{
            color: themePalette.redMedium,
          }}
        />
      );
    }
    return (
      <RadioButtonUncheckedIcon
        sx={{
          color: themePalette.greyLight,
        }}
      />
    );
  };

  const getStepAction = () => {
    const actions = {
      [KitStepper.Order]: handleNext,
      [KitStepper.Confirmation]: handleSubmit,
      [KitStepper.Complete]: internalClose,
    };
    return actions[activeStep];
  };

  const getStepLabel = () => {
    const labels = {
      [KitStepper.Order]: "Next",
      [KitStepper.Confirmation]: "Confirm",
      [KitStepper.Complete]: "Close",
    };
    return labels[activeStep];
  };

  return (
    <Dialog
      open={newKitPickupOpen}
      onClose={internalClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onExited: () => {
          if (activeStep === KitStepper.Complete) {
            // Clear all order form state if we just completed an order
            resetFormState();
          }
          setActiveStep(KitStepper.Order);
          resetErrorDetails();
        },
      }}
      data-testid="kit-pickup-modal"
    >
      <NewKitPickupDialogTitle id="customized-dialog-title">
        <TextWrap>
          {activeStep === KitStepper.Confirmation
            ? "CONFIRM KIT PICKUP"
            : "REQUEST KIT PICKUP"}
        </TextWrap>
      </NewKitPickupDialogTitle>
      <DialogContent>
        {activeStep === KitStepper.Complete && newKitPickupOrder ? (
          <KitPickupConfirmation
            newKitPickupOrder={newKitPickupOrder}
            newKitPickupClinicAccount={newKitPickupClinicAccount}
            newOrderCode={newOrderCode}
          />
        ) : (
          <KitPickupInformation
            auth={auth}
            newKitPickupClinicAccount={newKitPickupClinicAccount}
            setNewKitPickupClinicAccount={setNewKitPickupClinicAccount}
            newKitPickupDetails={newKitPickupDetails}
            setNewKitPickupDetails={setNewKitPickupDetails}
            setLoggedOut={setLoggedOut}
            errorDetails={errorDetails}
            setErrorDetails={setErrorDetails}
            resetErrorDetails={resetErrorDetails}
            activeStep={activeStep}
          />
        )}
      </DialogContent>
      <KitPickupStyledDialogActions>
        {activeStep !== KitStepper.Complete ? (
          <KitPickupBackButton
            label={activeStep === KitStepper.Order ? "Cancel" : "Back"}
            onClick={handleBackOrCancel}
            size="medium"
          />
        ) : (
          <div />
        )}
        <StepperWrapper activeStep={activeStep}>
          {getSteps().map((label, index) => {
            if (index < 2) {
              return (
                <Step key={label}>
                  <StepLabel StepIconComponent={StepIcon} />
                </Step>
              );
            }
          })}
        </StepperWrapper>
        <Button
          label={getStepLabel()}
          onClick={getStepAction()}
          size="medium"
          disabled={isSubmitting || errorDetails.isInternationalAccountError}
        />
      </KitPickupStyledDialogActions>
    </Dialog>
  );
};

export default NewKitPickup;
